import { Web3DViewer } from '@technology/web3d';
import {
  PlanarImageStation,
  PositioningData,
  StationsCapabilityProvider,
  Web3dTool,
} from '@trimble/web3d-providers';
import { Accessor } from 'solid-js';
import {
  PlanarControls,
  PlanarModel,
  PlanarPlugin,
} from '@technology/web3d-plugin-planar';
import { Web3dSettings } from '../models/web3d-settings';
import {
  ImageCapabilityBase,
  type ImageCapabilityOpacityOptions,
} from './image-capability-base';
import { NavigationTool } from '../helpers/web3d-tool-helper';
import { ThreeJsManager } from '@trimble/web3d-utils';
import {
  computeCenterRayTarget,
  computePlanarLookAtQuaternion,
} from '../helpers/camera/planar-frustum-helpers';

// The current fov ranges are too tight, so the user can't rotate to the edge.
// This multiplier is used to increase the fov ranges a small amount.
export const PLANAR_FOV_MULTIPLIER = 1.1;
export const PLANAR_PADDING = 1;

export class PlanarCapability extends ImageCapabilityBase {
  protected override requiredWeb3DTools: NavigationTool[] = [
    Web3dTool.PlanarControls,
  ];

  constructor(
    stationsProvider: StationsCapabilityProvider,
    viewer: Web3DViewer,
    private readonly planarPlugin: PlanarPlugin,
    cameraName: string,
    web3dSettings: Accessor<Web3dSettings>,
    threeJsManager: ThreeJsManager,
    opacityOptions?: ImageCapabilityOpacityOptions
  ) {
    super(
      stationsProvider,
      cameraName,
      viewer,
      web3dSettings,
      threeJsManager,
      opacityOptions
    );

    // Render planar behind the point cloud
    this.planarPlugin.renderOrder = -1;
  }

  public async loadImageStation(
    imageStation: PlanarImageStation
  ): Promise<PlanarModel | undefined> {
    if (!this.isActive() || imageStation.station.tilesUrl === '') {
      return Promise.resolve(undefined);
    }

    const model =
      this.viewer.getModel(imageStation.station.id, PlanarModel) ??
      (await this.planarPlugin.load(imageStation.station));

    model.setDepthTest(false);

    return model;
  }

  public setupCameraForStation(imageStation: PlanarImageStation): void {
    const planarModel: PlanarModel | undefined = this.viewer.getModel(
      imageStation.station.id
    );

    if (!planarModel) {
      return;
    }

    const planarControls = this.viewer.tools.get(PlanarControls)!;
    planarControls.setupCamera(imageStation.station, PLANAR_PADDING);
  }

  /**
   * Planar cameras always look at the world principal point via
   * PlanarControls.setupCamera, so trajectory-based rotation must
   * not be applied — it would skew the camera away from the image.
   */
  protected override adjustCameraForTrajectoryChange(
    _oldTrajectoryAngle: number,
    _newTrajectoryAngle: number
  ): void {
    // no-op for planar
  }

  public override getFrustumPosition(): Omit<
    PositioningData,
    'cameraPosition'
  > {
    const basePosition = super.getFrustumPosition();
    if (!basePosition.targetPosition) {
      return basePosition;
    }

    const { camera } = this.viewer;
    const centerRayTarget = computeCenterRayTarget(
      camera.projectionMatrix,
      camera.matrixWorld
    );

    const quaternion = computePlanarLookAtQuaternion(
      camera.position,
      centerRayTarget
    );

    return {
      ...basePosition,
      quaternion: quaternion.toArray(),
    };
  }

  protected override shouldShowStationMarkers(): boolean {
    return false;
  }
}
